/**
 * useRequisitionStream — SSE for requisition changes (kitchen flow, lane C).
 * Modelled on lib/kitchenStream.ts: token via ?token= (EventSource cannot set
 * headers), exponential backoff capped at 30s. On every (re)connect the caller
 * is told to refetch, which is how an event missed while offline is recovered.
 *
 * Watchdog (pilot fix, Batch 5 lane tablet): on weak wifi a connection can die
 * without the browser noticing, and the screen kept saying "Live" while no
 * update could arrive. The server now sends a named `ping` event every 20s;
 * if nothing at all arrives for STALE_AFTER_MS the stream is dropped and
 * reopened, showing "Reconnecting…" meanwhile. Callers also keep a slow poll
 * while live (pollInterval) as a last safety net.
 */
import { useEffect, useRef, useState } from 'react'

export type RequisitionStreamStatus = 'connecting' | 'live' | 'reconnecting' | 'offline'

/** No message (event, ping or ready) for this long → the stream is dead. */
export const STALE_AFTER_MS = 45_000
/** A slow poll even while live; the normal poll when not. */
export const LIVE_POLL_MS = 150_000
export const OFFLINE_POLL_MS = 60_000

/** How often a screen polls its list, given the stream's state. */
export function pollInterval(status: RequisitionStreamStatus): number {
  return status === 'live' ? LIVE_POLL_MS : OFFLINE_POLL_MS
}

/**
 * Fires onStale once when beat() has not been called for timeoutMs. Pure
 * timers, so it is unit-tested with fake timers (requisitionStream.test.ts).
 */
export class StreamWatchdog {
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private readonly timeoutMs: number,
    private readonly onStale: () => void,
  ) {}

  /** A sign of life: restart the countdown. */
  beat(): void {
    this.stop()
    this.timer = setTimeout(() => {
      this.timer = null
      this.onStale()
    }, this.timeoutMs)
  }

  stop(): void {
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
  }
}

export interface RequisitionStreamEvent {
  type: string
  requisition_id: string
  req_no?: string
  kind?: 'issue' | 'return'
  status?: string
  emitted_at?: string
}

function streamURL(token: string): string {
  const fromEnv = import.meta.env?.VITE_API_URL
  let base = typeof fromEnv === 'string' && fromEnv.trim() ? fromEnv.trim() : 'http://localhost:8080/api/v1'
  base = base.replace(/\/+$/, '')
  if (!/\/api\/v1$/i.test(base)) base = `${base}/api/v1`
  return `${base}/inventory-flow/stream?token=${encodeURIComponent(token)}`
}

const MIN_BACKOFF = 1_000
const MAX_BACKOFF = 30_000

export function useRequisitionStream(opts: {
  enabled: boolean
  onEvent?: (ev: RequisitionStreamEvent) => void
  onReconnect?: () => void
}): RequisitionStreamStatus {
  const { enabled } = opts
  const [status, setStatus] = useState<RequisitionStreamStatus>('connecting')
  const onEventRef = useRef(opts.onEvent)
  const onReconnectRef = useRef(opts.onReconnect)
  useEffect(() => {
    onEventRef.current = opts.onEvent
    onReconnectRef.current = opts.onReconnect
  }, [opts.onEvent, opts.onReconnect])

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') {
      setStatus('offline')
      return
    }
    let stopped = false
    let es: EventSource | null = null
    let timer: number | null = null
    let backoff = MIN_BACKOFF
    // The stream went quiet: drop it and open a new one straight away.
    const watchdog = new StreamWatchdog(STALE_AFTER_MS, () => {
      if (stopped) return
      setStatus('reconnecting')
      es?.close()
      es = null
      connect(true)
    })

    const connect = (reconnecting = false) => {
      if (stopped) return
      let token: string | null = null
      try {
        token = localStorage.getItem('pos_token')
      } catch {
        token = null
      }
      if (!token) {
        watchdog.stop()
        setStatus('offline')
        return
      }
      if (!reconnecting) setStatus('connecting')
      try {
        es = new EventSource(streamURL(token))
      } catch {
        schedule()
        return
      }
      // Opening counts as the first sign of life; from then on any message does.
      watchdog.beat()
      es.addEventListener('ready', () => {
        watchdog.beat()
        backoff = MIN_BACKOFF
        setStatus('live')
        onReconnectRef.current?.()
      })
      es.addEventListener('ping', () => watchdog.beat())
      es.onmessage = () => watchdog.beat()
      es.addEventListener('requisition', (raw) => {
        watchdog.beat()
        try {
          onEventRef.current?.(JSON.parse((raw as MessageEvent).data) as RequisitionStreamEvent)
        } catch {
          /* ignore malformed payload */
        }
      })
      es.onerror = () => {
        watchdog.stop()
        setStatus('offline')
        es?.close()
        es = null
        schedule()
      }
    }
    const schedule = () => {
      if (stopped) return
      const delay = backoff
      backoff = Math.min(MAX_BACKOFF, backoff * 2)
      timer = window.setTimeout(() => connect(), delay)
    }
    connect()
    return () => {
      stopped = true
      watchdog.stop()
      if (timer) window.clearTimeout(timer)
      es?.close()
    }
  }, [enabled])

  return status
}
